import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../api';
import { environment } from '../../environments/environment';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import HubIcon from '@mui/icons-material/Hub';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { CatalogColumns, CatalogGroup, useCollapsedGroups } from './CatalogGroups';
import { Box, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import EntityDetailDrawer from './EntityDetailDrawer';
import MarkdownCardPreview from './MarkdownCardPreview';
import AgentConfigurations, { AgentRelatedTemplates, AgentConcepts, AgentPolicies, AgentParameters, TaskConfigurationItem, RelatedActivityTemplate, ActivityContext } from './AgentConfigurations';

import { clearStoredAuthTokens } from '../utils/auth';
import { clearProjectCache } from '../services/projectService';
import { toTitleCase, UNCATEGORIZED } from '../utils/textFormat';
import { keepingNavState } from '../utils/searchParamsNavigation';

import Sidebar from './Sidebar';
import { dsVar, ink } from '../theme/tokens';

interface Operator {
    _id?: string;
    id: string;
    operator_name: string;
    operator_description: string;
    operator_code?: string;
    flow_template_code?: string;
    operator_type?: string;
    operator_type_name?: string;
    functionalDomains?: { code: string; name: string }[];
    industrySectors?: { code: string; name: string }[];
}

interface Project {
    _id?: string;
    id?: string;
    projectName: string;
    projectDescription?: string;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

/**
 * One entry in the agent drawer's navigation stack. `name` (and, for cards,
 * `description`) are known before the detail fetch resolves, so the drawer can
 * render its header immediately; `detail` is filled in once loaded.
 */
interface AgentView {
    code: string;
    name: string;
    activityType?: string;
    description?: string;
    detail: ActivityTemplate | null;
    context: ActivityContext | null;
}

interface ActivityTemplate {
    id?: string;
    activity_template_code: string;
    activity_template_name: string;
    activity_template_description?: string;
    activity_type?: string;
    task_type?: string;
    related_activity_templates?: RelatedActivityTemplate[];
    configuration?: { task_configuration?: TaskConfigurationItem[] };
}

type CatalogTab = 'workflows' | 'agents';

const CATALOG_TABS: { key: CatalogTab; label: string }[] = [
    { key: 'workflows', label: 'Agentic workflows' },
    { key: 'agents', label: 'Agents' },
];

/**
 * The tab, kept in the query string (`?tab=agents`) so a link can open it directly.
 * An `?agent=` deep link — the Ontology screens' way back to an agent — always
 * lands on Agents, since that is the tab its drawer belongs to.
 */
const catalogTabFromParams = (params: URLSearchParams): CatalogTab =>
    params.get('tab') === 'agents' || params.get('agent') ? 'agents' : 'workflows';

// Wide enough for two roomy columns in the page's 7xl content width, and one once
// the docked agent drawer takes its ~440px.
const CATALOG_MIN_COLUMN_WIDTH = '26rem';

const OperatorsMarketplace = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { projectId, hideCustomize } = location.state || {};

    const [operators, setOperators] = useState<Operator[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState<Operator | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [detailOperator, setDetailOperator] = useState<{ operator: Operator; showCustomize: boolean } | null>(null);
    // Tracked by object identity, not id — operator/project rows do not reliably carry _id or id.
    const [addingOperator, setAddingOperator] = useState<Operator | null>(null);
    const [addingProject, setAddingProject] = useState<Project | null>(null);

    // Agents catalog
    const [agents, setAgents] = useState<ActivityTemplate[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(true);
    // Drawer navigation stack — empty means closed, last entry is what's displayed.
    const [agentStack, setAgentStack] = useState<AgentView[]>([]);
    const [agentDetailLoading, setAgentDetailLoading] = useState(false);
    const [agentDetailError, setAgentDetailError] = useState<string | null>(null);

    const currentAgentView = agentStack.length > 0 ? agentStack[agentStack.length - 1] : null;
    const previousAgentView = agentStack.length > 1 ? agentStack[agentStack.length - 2] : null;
    const agentDrawerOpen = Boolean(currentAgentView);

    // Type groups the user has folded away, keyed `<section>:<label>` so a label
    // shared by both catalogs folds independently. Everything starts expanded.
    const { isCollapsed, toggle: toggleGroup } = useCollapsedGroups();

    const activeTab = catalogTabFromParams(searchParams);
    const selectTab = (tab: CatalogTab) => {
        if (tab === activeTab) return;
        // The agent drawer belongs to the Agents tab, so switching tabs closes it —
        // and drops ?agent=, which would otherwise pull the page back to Agents.
        setAgentStack([]);
        setAgentDetailError(null);
        setAgentDetailLoading(false);
        const next = new URLSearchParams(searchParams);
        next.delete('agent');
        next.set('tab', tab);
        // keepingNavState: projectId / hideCustomize arrive in location.state.
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    };
    // Shown on each tab once loaded, so the hidden catalog's size is still visible.
    const tabCounts: Record<CatalogTab, number | null> = {
        workflows: loading ? null : (pagination?.total ?? operators.length),
        agents: agentsLoading ? null : agents.length,
    };

    useEffect(() => {
        const fetchOperators = async () => {
            setLoading(true);
            try {
                const orgId = localStorage.getItem('org_id');
                if (!orgId) {
                    console.error("No org_id found in local storage");
                    setLoading(false);
                    return;
                }

                const response = await axiosInstance.get(
                    `/operators/system?limit=10&page=${page}`
                );

                if (response.data) {
                    if (response.data.operators) {
                        setOperators(response.data.operators);
                    }
                    if (response.data.pagination) {
                        setPagination(response.data.pagination);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch operators:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOperators();
    }, [page]);

    useEffect(() => {
        const fetchAgents = async () => {
            setAgentsLoading(true);
            try {
                const response = await axiosInstance.get('/activity-templates', {
                    params: { activity_class: 'AGENT', scope_level: 'INDIVIDUAL', latest_only: true }
                });
                setAgents(response.data?.activity_templates ?? []);
            } catch (error) {
                console.error("Failed to fetch agents:", error);
                setAgents([]);
            } finally {
                setAgentsLoading(false);
            }
        };

        fetchAgents();
    }, []);

    const handleNextPage = () => {
        if (pagination && page < pagination.pages) {
            setPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            setPage(prev => prev - 1);
        }
    };

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleSignOut = () => {
        clearStoredAuthTokens();
        clearProjectCache();
        handleUserMenuClose();
        navigate('/login');
    };

    const createCampaign = async (operator: Operator, targetProjectId: string) => {
        setAddingOperator(operator);
        try {
            const payload = {
                project_id: targetProjectId,
                campaign_name: operator.operator_name,
                operator_code: operator.operator_code,
                campaign_description: operator.operator_description
            };

            const response = await axiosInstance.post('/campaigns', payload);
            if (response.data && response.data.campaign) {
                // Leave the spinner running through the navigation — this component unmounts.
                setShowProjectModal(false);
                setSelectedOperator(null);
                navigate(`/analytics/projects/${targetProjectId}`);
                return;
            }
            console.error("Campaign creation returned no campaign");
        } catch (error) {
            console.error("Failed to create campaign:", error);
        }
        setAddingOperator(null);
        setAddingProject(null);
    };

    const fetchProjectsForModal = async () => {
        setProjectsLoading(true);
        try {
            const orgId = localStorage.getItem('org_id');
            if (!orgId) {
                console.error("No org_id found in local storage");
                setProjects([]);
                return;
            }

            const response = await axiosInstance.get(
                `/projects?orgId=${orgId}&page=1&limit=10`
            );

            if (response.data && response.data.projects) {
                setProjects(response.data.projects);
            } else {
                setProjects([]);
            }
        } catch (error) {
            console.error("Failed to fetch projects:", error);
            setProjects([]);
        } finally {
            setProjectsLoading(false);
        }
    };

    const closeProjectModal = () => {
        if (addingProject) return;
        setShowProjectModal(false);
        setSelectedOperator(null);
    };

    const handleSelectProject = (project: Project) => {
        if (!selectedOperator || addingProject) return;
        // The modal stays open, showing a spinner on the row, until the campaign is created.
        setAddingProject(project);
        createCampaign(selectedOperator, (project._id || project.id) as string);
    };

    /**
     * Operators grouped by the operator_type_name the operators API denormalizes
     * from the flow template, falling back to a title-cased operator_type and then
     * "Uncategorized". Groups are alphabetical with "Uncategorized" pinned last,
     * matching the Agents catalog below.
     */
    const groupedOperators = useMemo(() => {
        const groups = new Map<string, Operator[]>();
        operators.forEach(operator => {
            // operator_type_name is already a display label; only the code needs title-casing.
            const label = operator.operator_type_name?.trim() || toTitleCase(operator.operator_type);
            const existing = groups.get(label);
            if (existing) {
                existing.push(operator);
            } else {
                groups.set(label, [operator]);
            }
        });
        return Array.from(groups.entries()).sort(([a], [b]) => {
            if (a === UNCATEGORIZED) return 1;
            if (b === UNCATEGORIZED) return -1;
            return a.localeCompare(b);
        });
    }, [operators]);

    /**
     * Agents grouped by task_type for display, falling back to activity_type and
     * then "Uncategorized" — the same chain the flow editors use to group these
     * templates. Groups are alphabetical with "Uncategorized" pinned last.
     */
    const groupedAgents = useMemo(() => {
        const groups = new Map<string, ActivityTemplate[]>();
        agents.forEach(agent => {
            const label = toTitleCase(agent.task_type || agent.activity_type);
            const existing = groups.get(label);
            if (existing) {
                existing.push(agent);
            } else {
                groups.set(label, [agent]);
            }
        });
        return Array.from(groups.entries()).sort(([a], [b]) => {
            if (a === UNCATEGORIZED) return 1;
            if (b === UNCATEGORIZED) return -1;
            return a.localeCompare(b);
        });
    }, [agents]);

    const openAgentDrawer = (agent: ActivityTemplate) => {
        // A fresh stack — a previous visit's detail must never leak into this one.
        setAgentStack([{
            code: agent.activity_template_code,
            name: agent.activity_template_name,
            activityType: agent.activity_type,
            description: agent.activity_template_description,
            detail: null,
            context: null,
        }]);
        setAgentDetailError(null);
    };

    const closeAgentDrawer = () => {
        setAgentStack([]);
        setAgentDetailError(null);
        setAgentDetailLoading(false);
        // Drop ?agent= so closing the drawer does not leave a URL that reopens it.
        if (searchParams.get('agent')) {
            const next = new URLSearchParams(searchParams);
            next.delete('agent');
            // keepingNavState: projectId / hideCustomize arrive in location.state
            // when this page is opened as "Add workflow" from a project, and a
            // bare options object would clear them — so closing the drawer would
            // quietly detach the page from the project it is adding to.
            setSearchParams(next, keepingNavState(location.state, { replace: true }));
        }
    };

    /**
     * Open the drawer for a template known only by code, and load it straight away.
     *
     * The entry point for `?agent=<code>` deep links — the Ontology screens link here to
     * close the round trip from a concept, policy or threshold back to the agents that
     * use it. Detail is fetched immediately because, unlike a grid click, there is no
     * card behind this to read a name or description from.
     */
    const openAgentByCode = (code: string) => {
        setAgentStack([{ code, name: code, detail: null, context: null }]);
        setAgentDetailError(null);
        fetchAgentDetail(code);
    };

    const fetchAgentDetail = async (code: string) => {
        setAgentDetailLoading(true);
        setAgentDetailError(null);

        // Both in flight together. The ontology context is supplementary, so it is
        // settled independently — a failure there must not block the template detail.
        const [detailResult, contextResult] = await Promise.allSettled([
            axiosInstance.get(`/activity-templates/${encodeURIComponent(code)}`),
            axiosInstance.get(`/ontology/activities/${encodeURIComponent(code)}/context`),
        ]);

        if (contextResult.status === 'rejected') {
            console.error("Failed to fetch activity context:", contextResult.reason);
        }

        if (detailResult.status === 'rejected') {
            console.error("Failed to fetch agent details:", detailResult.reason);
            setAgentDetailError("Could not load details. Please try again.");
            setAgentDetailLoading(false);
            return;
        }

        const detail: ActivityTemplate | null = detailResult.value.data?.activity_template ?? null;
        const context: ActivityContext | null = contextResult.status === 'fulfilled'
            ? (contextResult.value.data?.context ?? contextResult.value.data ?? null)
            : null;

        setAgentStack(prev => {
            const top = prev[prev.length - 1];
            // Discard the response if the user navigated elsewhere while it was in flight.
            if (!top || top.code !== code) return prev;
            return [...prev.slice(0, -1), {
                ...top,
                detail,
                context,
                activityType: detail?.activity_type ?? top.activityType,
                // A view opened by code alone starts with the code as its title; adopt
                // the real name once the detail supplies one.
                name: detail?.activity_template_name || top.name,
                description: detail?.activity_template_description ?? top.description,
            }];
        });
        setAgentDetailLoading(false);
    };

    /**
     * Open the drawer when the URL names an agent.
     *
     * Keyed on the param CHANGING rather than on the drawer's state: a user who arrives
     * by deep link and then clicks a different card in the grid must not be dragged back
     * to the linked agent on the next render. The ref records what was last acted on.
     */
    const handledAgentParam = useRef<string | null>(null);
    const agentParam = searchParams.get('agent');

    useEffect(() => {
        if (!agentParam) {
            handledAgentParam.current = null;
            return;
        }
        if (handledAgentParam.current === agentParam) return;
        handledAgentParam.current = agentParam;
        openAgentByCode(agentParam);
    }, [agentParam]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleViewAgentDetails = () => {
        if (!currentAgentView || agentDetailLoading) return;
        fetchAgentDetail(currentAgentView.code);
    };

    /** Push a related template onto the stack and load it straight away — a
     *  related entry carries only a code and a name, so there is nothing to show
     *  until its detail resolves. */
    const openRelatedTemplate = (related: RelatedActivityTemplate) => {
        if (agentDetailLoading) return;
        setAgentStack(prev => [...prev, {
            code: related.activity_template_code,
            name: related.activity_template_name || related.activity_template_code,
            detail: null,
            context: null,
        }]);
        setAgentDetailError(null);
        fetchAgentDetail(related.activity_template_code);
    };

    const goBackAgentView = () => {
        if (agentDetailLoading) return;
        setAgentStack(prev => prev.slice(0, -1));
        setAgentDetailError(null);
    };

    const handleHire = (operator: Operator) => {
        if (addingOperator) return;
        if (hideCustomize && projectId) {
            createCampaign(operator, projectId);
        } else {
            setSelectedOperator(operator);
            setShowProjectModal(true);
            fetchProjectsForModal();
        }
    };

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
            <Sidebar activePage="marketplace" forceCollapsed={agentDrawerOpen} />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Navigation Bar */}
                <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
                    <div className="flex-1 max-w-2xl text-left">
                        <div className="relative group flex items-center justify-start h-full">
                            <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
                            <input
                                className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none"
                                placeholder="Search analysts..."
                                type="text"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 ml-8">
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <NotificationsIcon />
                        </button>
                        <div
                            className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                            onClick={handleUserMenuOpen}
                            aria-label="Open user menu"
                        >
                            <AccountCircleIcon />
                        </div>
                        <Menu
                            anchorEl={userMenuAnchorEl}
                            open={Boolean(userMenuAnchorEl)}
                            onClose={handleUserMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: dsVar('surface'),
                                    color: dsVar('fg-body'),
                                    border: `1px solid ${dsVar('line')}`,
                                    minWidth: '180px',
                                    mt: 1
                                }
                            }}
                        >
                            <MenuItem
                                disabled
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: dsVar('fg-2'),
                                    borderBottom: `1px solid ${dsVar('line')}`,
                                    opacity: '1 !important'
                                }}
                            >
                                {localStorage.getItem('org_id') || 'User'}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleUserMenuClose();
                                    navigate('/access-control');
                                }}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    fontSize: '0.9rem',
                                    color: dsVar('fg-body'),
                                    '&:hover': {
                                        backgroundColor: dsVar('fg', 0.08)
                                    }
                                }}
                            >
                                <AccountCircleIcon fontSize="small" />
                                Your account
                            </MenuItem>
                            <MenuItem
                                onClick={handleSignOut}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    fontSize: '0.9rem',
                                    color: dsVar('accent-fg'),
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 222, 89, 0.08)'
                                    }
                                }}
                            >
                                <LogoutIcon fontSize="small" />
                                Sign out
                            </MenuItem>
                        </Menu>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto">

                    {/* Main Content Area */}
                    <main className="flex-1 flex flex-col p-6 lg:p-10 w-full xl:max-w-7xl mx-auto m-0 text-left">
                        {/* Hero Section */}
                        <div className="mb-10 flex flex-col gap-4 m-0 text-left items-start">
                            <h1 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-fg m-0 mr-auto p-0 font-mono">
                                Agentic <span className="text-fg font-normal m-0" style={{ fontFamily: 'Inter, sans-serif' }}>Hub</span>
                            </h1>
                            <p className="text-fg-2 text-lg max-w-2xl font-light leading-relaxed m-0 p-0 tracking-normal font-sans">
                                Meet your AI-powered Digital Workers — ready to deploy, built for your industry, and designed to work alongside you. Pick a analyst, put it to work, and start turning your business data into decisions that matter.
                            </p>
                        </div>


                        {/* Catalog tabs — the same pill toggle as a workflow's Insights / Run health / Explainability. */}
                        <div className="mb-8 flex m-0">
                            <div role="tablist" aria-label="Catalogs" className="bg-surface p-1 rounded-full border border-line flex gap-1">
                                {CATALOG_TABS.map(tab => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeTab === tab.key}
                                        onClick={() => selectTab(tab.key)}
                                        className={`px-8 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-none cursor-pointer flex items-center gap-2 ${activeTab === tab.key
                                            ? 'bg-accent text-on-accent'
                                            : 'text-fg-2 hover:text-fg-body bg-transparent'
                                            }`}
                                    >
                                        {tab.label}
                                        {tabCounts[tab.key] !== null && <span className="opacity-60 m-0">{tabCounts[tab.key]}</span>}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {activeTab === 'workflows' && (
                            <>
                                {/* Cards, grouped by operator type */}
                                {loading ? (
                                    <p className="text-fg-2 mb-8">Loading analysts...</p>
                                ) : groupedOperators.length === 0 ? (
                                    <p className="text-fg-2 mb-8">No analysts found.</p>
                                ) : (
                                    <CatalogColumns minColumnWidth={CATALOG_MIN_COLUMN_WIDTH} spacing="roomy">
                                        {groupedOperators.map(([operatorType, groupOperators]) => (
                                            <CatalogGroup
                                                key={operatorType}
                                                label={operatorType}
                                                count={groupOperators.length}
                                                collapsed={isCollapsed(`workflows:${operatorType}`)}
                                                onToggle={() => toggleGroup(`workflows:${operatorType}`)}
                                            >
                                                {groupOperators.map(operator => (
                                                    <div key={operator.operator_code ?? operator.id} onClick={() => setDetailOperator({ operator, showCustomize: false })} className="group relative bg-page border border-line rounded-lg overflow-hidden hover:border-accent-fg/50 transition-all duration-300 m-0 cursor-pointer">
                                                        <div className="p-5 flex flex-col gap-3 h-full m-0 text-left items-start">
                                                            <div className="flex items-start gap-3 w-full m-0">
                                                                <div className="w-10 h-10 shrink-0 rounded-lg bg-accent/20 border border-accent-fg/30 flex items-center justify-center text-accent-fg group-hover:scale-110 transition-transform m-0">
                                                                    <PsychologyIcon className="m-0" />
                                                                </div>
                                                                <h4 className="text-base font-bold text-fg leading-snug m-0 min-w-0 flex-1 self-center font-mono line-clamp-2">{operator.operator_name}</h4>
                                                            </div>
                                                            <MarkdownCardPreview
                                                                text={operator.operator_description}
                                                                className="text-fg-2 text-sm leading-relaxed m-0 font-sans tracking-tight line-clamp-3"
                                                            />
                                                            <div className="mt-auto pt-1 flex items-center justify-end w-full m-0">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleHire(operator); }}
                                                                    disabled={Boolean(addingOperator)}
                                                                    className={`shrink-0 hover:bg-opacity-90 text-on-accent text-sm font-bold h-9 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors border-none m-0 ${addingOperator && addingOperator !== operator ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                                    style={{ backgroundColor: dsVar('accent') }}
                                                                >
                                                                    {addingOperator === operator ? (
                                                                        <>
                                                                            <span className="inline-block w-3.5 h-3.5 border-2 border-page/40 border-t-page rounded-full animate-spin" />
                                                                            <span className="m-0">Adding...</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <RocketLaunchIcon fontSize="small" className="m-0" />
                                                                            <span className="m-0">Add</span>
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </CatalogGroup>
                                        ))}
                                    </CatalogColumns>
                                )}

                                {/* Pagination Controls */}
                                {!loading && pagination && pagination.pages > 1 && (
                                    <div className="flex items-center justify-center gap-4 mt-8 m-0 w-full">
                                        <button
                                            onClick={handlePrevPage}
                                            disabled={page <= 1}
                                            className={`flex items-center justify-center px-4 py-2 rounded-lg font-bold border transition-colors ${page <= 1 ? 'border-fg/10 text-fg/30 cursor-not-allowed bg-transparent' : 'border-accent-fg/50 text-accent-fg hover:bg-accent/10 cursor-pointer bg-transparent'}`}
                                        >
                                            <ArrowBackIcon fontSize="small" className="mr-2 m-0" />
                                            Previous
                                        </button>
                                        <div className="flex items-center justify-center px-4">
                                            <span className="text-fg-2 text-sm font-bold font-mono border-x py-1 px-4 border-fg/10 m-0">Page {page} of {pagination.pages}</span>
                                        </div>
                                        <button
                                            onClick={handleNextPage}
                                            disabled={page >= pagination.pages}
                                            className={`flex items-center justify-center px-4 py-2 rounded-lg font-bold border transition-colors ${page >= pagination.pages ? 'border-fg/10 text-fg/30 cursor-not-allowed bg-transparent' : 'border-accent-fg/50 text-accent-fg hover:bg-accent/10 cursor-pointer bg-transparent'}`}
                                        >
                                            Next
                                            <ArrowForwardIcon fontSize="small" className="ml-2 m-0" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'agents' && (
                            agentsLoading ? (
                                <p className="text-fg-2 mb-8">Loading agents...</p>
                            ) : groupedAgents.length === 0 ? (
                                <p className="text-fg-2 mb-8">No agents found.</p>
                            ) : (
                                <CatalogColumns minColumnWidth={CATALOG_MIN_COLUMN_WIDTH} spacing="roomy">
                                    {groupedAgents.map(([taskType, groupAgents]) => (
                                        <CatalogGroup
                                            key={taskType}
                                            label={taskType}
                                            count={groupAgents.length}
                                            collapsed={isCollapsed(`agents:${taskType}`)}
                                            onToggle={() => toggleGroup(`agents:${taskType}`)}
                                        >
                                            {groupAgents.map(agent => (
                                                <div key={agent.activity_template_code} onClick={() => openAgentDrawer(agent)} className="group relative bg-page border border-line rounded-lg overflow-hidden hover:border-accent-fg/50 transition-all duration-300 m-0 cursor-pointer">
                                                    <div className="p-5 flex flex-col gap-3 h-full m-0 text-left items-start">
                                                        <div className="flex items-start gap-3 w-full m-0">
                                                            <div className="w-10 h-10 shrink-0 rounded-lg bg-accent/20 border border-accent-fg/30 flex items-center justify-center text-accent-fg group-hover:scale-110 transition-transform m-0">
                                                                <SmartToyIcon className="m-0" />
                                                            </div>
                                                            <h4 className="text-base font-bold text-fg leading-snug m-0 min-w-0 flex-1 self-center font-mono line-clamp-2">{agent.activity_template_name}</h4>
                                                        </div>
                                                        <MarkdownCardPreview
                                                            text={agent.activity_template_description ?? ''}
                                                            className="text-fg-2 text-sm leading-relaxed m-0 font-sans tracking-tight line-clamp-3"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </CatalogGroup>
                                    ))}
                                </CatalogColumns>
                            )
                        )}
                    </main>
                </div>
            </div>

            {/* Operator Detail Drawer */}
            <EntityDetailDrawer
                open={Boolean(detailOperator)}
                onClose={() => setDetailOperator(null)}
                title={detailOperator?.operator.operator_name ?? ''}
                subtitle={detailOperator?.operator.functionalDomains?.[0]?.code}
                description={detailOperator?.operator.operator_description ?? ''}
                actions={detailOperator && (
                    detailOperator.showCustomize ? (
                        <button
                            onClick={() => {
                                setDetailOperator(null);
                                navigate('/AIStudio/customize', {
                                    state: {
                                        flow_template_code: detailOperator.operator.flow_template_code,
                                        operator_id: detailOperator.operator._id || detailOperator.operator.id,
                                        operator_name: detailOperator.operator.operator_name,
                                        fromOperator: true,
                                        returnTo: { pathname: '/operators-marketplace' }
                                    }
                                });
                            }}
                            className="w-full bg-transparent border border-accent-fg text-accent-fg hover:bg-accent/10 font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer"
                        >
                            <SettingsIcon fontSize="small" />
                            Customize
                        </button>
                    ) : (
                        <button
                            onClick={() => { const operator = detailOperator.operator; setDetailOperator(null); handleHire(operator); }}
                            disabled={Boolean(addingOperator)}
                            className={`w-full font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors border-none ${addingOperator ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            style={{ backgroundColor: dsVar('accent'), color: dsVar('on-accent') }}
                        >
                            <RocketLaunchIcon fontSize="small" />
                            Add
                        </button>
                    )
                )}
            />

            {/* Agent Detail Drawer */}
            <EntityDetailDrawer
                docked
                open={agentDrawerOpen}
                onClose={closeAgentDrawer}
                icon={<SmartToyIcon />}
                title={currentAgentView?.name ?? ''}
                subtitle={currentAgentView?.activityType}
                description={currentAgentView?.detail?.activity_template_description ?? currentAgentView?.description ?? ''}
                banner={previousAgentView && (
                    // Styled with sx, not Tailwind: the drawer portals outside #root,
                    // where this project's `important: '#root'` utilities do not apply.
                    <Box sx={{ mb: 3 }}>
                        <Box
                            component="button"
                            type="button"
                            onClick={goBackAgentView}
                            disabled={agentDetailLoading}
                            sx={{
                                maxWidth: '100%',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                py: 0.75,
                                px: 1.25,
                                borderRadius: '8px',
                                border: `1px solid ${dsVar('line')}`,
                                backgroundColor: dsVar('page'),
                                color: dsVar('fg-2'),
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: '0.6875rem',
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                lineHeight: 1.4,
                                cursor: 'pointer',
                                transition: 'color 150ms, border-color 150ms',
                                '&:hover': { color: dsVar('accent-fg'), borderColor: 'rgba(255, 222, 89, 0.4)' },
                                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Back to {previousAgentView.name}
                            </Box>
                        </Box>
                    </Box>
                )}
                actions={currentAgentView && !currentAgentView.detail && (
                    <>
                        {agentDetailError && (
                            <Typography component="p" sx={{ color: ink('#F87171'), fontSize: '0.75rem', m: 0 }}>
                                {agentDetailError}
                            </Typography>
                        )}
                        <Box
                            component="button"
                            type="button"
                            onClick={handleViewAgentDetails}
                            disabled={agentDetailLoading}
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                py: 1.25,
                                px: 2,
                                borderRadius: '8px',
                                border: `1px solid ${dsVar('accent-fg')}`,
                                backgroundColor: 'transparent',
                                color: dsVar('accent-fg'),
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'background-color 150ms',
                                '&:hover': { backgroundColor: 'rgba(255, 222, 89, 0.1)' },
                                '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
                            }}
                        >
                            {agentDetailLoading ? (
                                <>
                                    <CircularProgress size={14} sx={{ color: dsVar('accent-fg') }} />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <InfoIcon fontSize="small" />
                                    {agentDetailError ? 'Retry' : 'View details'}
                                </>
                            )}
                        </Box>
                    </>
                )}
            >
                {currentAgentView?.detail ? (
                    <>
                        <AgentConfigurations items={currentAgentView.detail.configuration?.task_configuration} />
                        <AgentRelatedTemplates
                            items={currentAgentView.detail.related_activity_templates}
                            onSelect={openRelatedTemplate}
                            disabled={agentDetailLoading}
                        />
                        <AgentConcepts items={currentAgentView.context?.concepts} />
                        <AgentPolicies items={currentAgentView.context?.policies} />
                        <AgentParameters
                            items={currentAgentView.context?.parameters?.in_force}
                            pendingDraftCount={currentAgentView.context?.parameters?.pending_draft_count}
                            activityCode={currentAgentView.code}
                        />
                    </>
                ) : agentDetailLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: dsVar('fg-2'), fontSize: '0.875rem' }}>
                        <CircularProgress size={14} sx={{ color: dsVar('accent-fg') }} />
                        Loading details...
                    </Box>
                ) : null}
            </EntityDetailDrawer>

            {/* Project Selection Modal */}
            {showProjectModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={closeProjectModal}
                >
                    <div
                        className="w-full max-w-md bg-surface border border-line rounded-xl shadow-2xl overflow-hidden m-0 text-left"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-4 p-6 border-b border-line m-0">
                            <h3 className="text-lg font-bold text-fg m-0 font-mono">
                                Add <span className="text-accent-fg">{selectedOperator?.operator_name}</span> to a project
                            </h3>
                            <button
                                onClick={closeProjectModal}
                                disabled={Boolean(addingProject)}
                                className={`text-fg-2 hover:text-fg bg-transparent border-none p-0 m-0 leading-none ${addingProject ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-6 max-h-80 overflow-y-auto m-0">
                            {projectsLoading ? (
                                <p className="text-fg-2 m-0">Loading projects...</p>
                            ) : projects.length > 0 ? (
                                <div className="flex flex-col gap-2 m-0">
                                    {projects.map(project => (
                                        <button
                                            key={project._id || project.id}
                                            onClick={() => handleSelectProject(project)}
                                            disabled={Boolean(addingProject)}
                                            className={`w-full text-left bg-page/60 border border-line rounded-lg p-4 transition-colors m-0 flex items-center justify-between gap-3 ${addingProject ? `cursor-not-allowed ${addingProject === project ? 'border-accent-fg/50' : 'opacity-50'}` : 'hover:border-accent-fg/50 hover:bg-page cursor-pointer'}`}
                                        >
                                            <div className="m-0 min-w-0">
                                                <p className="text-fg font-semibold text-sm m-0 font-mono">{project.projectName}</p>
                                                {project.projectDescription && (
                                                    <p className="text-fg-2 text-xs mt-1 m-0 line-clamp-2 overflow-hidden text-ellipsis">{project.projectDescription}</p>
                                                )}
                                            </div>
                                            {addingProject === project && (
                                                <span className="inline-block w-4 h-4 shrink-0 border-2 border-accent-fg/30 border-t-accent-fg rounded-full animate-spin" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-fg-2 m-0">No projects found.</p>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-3 p-6 border-t border-line m-0">
                            {addingProject ? (
                                <span className="text-fg-2 text-xs m-0">Adding workflow to project...</span>
                            ) : (
                                <span className="m-0" />
                            )}
                            <button
                                onClick={closeProjectModal}
                                disabled={Boolean(addingProject)}
                                className={`bg-transparent border border-line text-fg-soft font-bold py-2 px-4 rounded-lg transition-colors m-0 ${addingProject ? 'opacity-40 cursor-not-allowed' : 'hover:text-fg hover:border-fg-2 cursor-pointer'}`}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OperatorsMarketplace;
